import React, { useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, BarChart3, Upload, Download, FileText, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

interface TextStats {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  sentences: number;
  paragraphs: number;
  lines: number;
  readingTime: number;
  speakingTime: number;
  mostCommonWords: Array<{ word: string; count: number }>;
  averageWordsPerSentence: number;
  averageSentencesPerParagraph: number;
  longestWord: string;
  shortestWord: string;
}

export const WordCounterTool: React.FC = () => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stats = useMemo((): TextStats => {
    if (!text.trim()) {
      return {
        characters: 0,
        charactersNoSpaces: 0,
        words: 0,
        sentences: 0,
        paragraphs: 0,
        lines: 0,
        readingTime: 0,
        speakingTime: 0,
        mostCommonWords: [],
        averageWordsPerSentence: 0,
        averageSentencesPerParagraph: 0,
        longestWord: '',
        shortestWord: ''
      };
    }

    const characters = text.length;
    const charactersNoSpaces = text.replace(/\s/g, '').length;
    const lines = text.split('\n').length;
    
    // Words calculation
    const words = text.trim().split(/\s+/).filter(word => word.length > 0);
    const wordCount = words.length;
    
    // Sentences calculation (split by . ! ? but not abbreviations)
    const sentences = text.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0);
    const sentenceCount = sentences.length;
    
    // Paragraphs calculation
    const paragraphs = text.split(/\n\s*\n/).filter(para => para.trim().length > 0);
    const paragraphCount = paragraphs.length;
    
    // Reading time (average 200 words per minute)
    const readingTime = Math.ceil(wordCount / 200);
    
    // Speaking time (average 150 words per minute)
    const speakingTime = Math.ceil(wordCount / 150);
    
    // Most common words
    const wordFrequency: { [key: string]: number } = {};
    words.forEach(word => {
      const cleanWord = word.toLowerCase().replace(/[^\w]/g, '');
      if (cleanWord.length > 2) { // Only count words longer than 2 characters
        wordFrequency[cleanWord] = (wordFrequency[cleanWord] || 0) + 1;
      }
    });
    
    const mostCommonWords = Object.entries(wordFrequency)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([word, count]) => ({ word, count }));
    
    // Average calculations
    const averageWordsPerSentence = sentenceCount > 0 ? Math.round((wordCount / sentenceCount) * 10) / 10 : 0;
    const averageSentencesPerParagraph = paragraphCount > 0 ? Math.round((sentenceCount / paragraphCount) * 10) / 10 : 0;
    
    // Longest and shortest words
    const cleanWords = words.map(word => word.replace(/[^\w]/g, '')).filter(word => word.length > 0);
    const longestWord = cleanWords.reduce((longest, current) => 
      current.length > longest.length ? current : longest, '');
    const shortestWord = cleanWords.reduce((shortest, current) => 
      current.length < shortest.length ? current : shortest, cleanWords[0] || '');

    return {
      characters,
      charactersNoSpaces,
      words: wordCount,
      sentences: sentenceCount,
      paragraphs: paragraphCount,
      lines,
      readingTime,
      speakingTime,
      mostCommonWords,
      averageWordsPerSentence,
      averageSentencesPerParagraph,
      longestWord,
      shortestWord
    };
  }, [text]);

  const copyToClipboard = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.failedToCopy'));
    }
  };

  const downloadStats = () => {
    const statsText = `Text Analysis Report
Generated on: ${new Date().toLocaleString()}

=== BASIC STATISTICS ===
Characters: ${stats.characters.toLocaleString()}
Characters (no spaces): ${stats.charactersNoSpaces.toLocaleString()}
Words: ${stats.words.toLocaleString()}
Sentences: ${stats.sentences.toLocaleString()}
Paragraphs: ${stats.paragraphs.toLocaleString()}
Lines: ${stats.lines.toLocaleString()}

=== READING METRICS ===
Estimated reading time: ${stats.readingTime} minute(s)
Estimated speaking time: ${stats.speakingTime} minute(s)

=== ADVANCED ANALYSIS ===
Average words per sentence: ${stats.averageWordsPerSentence}
Average sentences per paragraph: ${stats.averageSentencesPerParagraph}
Longest word: ${stats.longestWord}
Shortest word: ${stats.shortestWord}

=== MOST COMMON WORDS ===
${stats.mostCommonWords.map((item, index) => 
  `${index + 1}. ${item.word} (${item.count} times)`
).join('\n')}

=== ORIGINAL TEXT ===
${text}`;

    const blob = new Blob([statsText], { type: 'text/plain' });
    saveAs(blob, 'text-analysis-report.txt');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setText(content);
      };
      reader.readAsText(file);
    }
  };

  const clearText = () => {
    setText('');
  };

  const sampleTexts = [
    "The quick brown fox jumps over the lazy dog. This pangram contains every letter of the alphabet at least once.",
    "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.",
    "In a hole in the ground there lived a hobbit. Not a nasty, dirty, wet hole, filled with the ends of worms and an oozy smell, nor yet a dry, bare, sandy hole with nothing in it to sit down on or to eat: it was a hobbit-hole, and that means comfort."
  ];

  const useSample = (sample: string) => {
    setText(sample);
  };

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BarChart3 className="w-5 h-5" />
            <span>{t('tools.wordCounter.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              onClick={() => text && copyToClipboard(text)}
              disabled={!text}
              variant="outline"
            >
              <Copy className="w-4 h-4 mr-2" />
              {t('tools.wordCounter.copyText')}
            </Button>
            <Button
              onClick={downloadStats}
              disabled={!text}
              variant="outline"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('tools.wordCounter.downloadReport')}
            </Button>
            <Button
              onClick={clearText}
              variant="outline"
            >
              {t('common.clear')}
            </Button>
          </div>

          <div className="mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.md,.doc,.docx"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="mb-2"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t('common.selectFile')}
            </Button>
          </div>

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter or paste your text here to analyze..."
            className="min-h-[200px]"
          />

          {/* Sample Texts */}
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Sample Texts:
            </h4>
            <div className="space-y-2">
              {sampleTexts.map((sample, index) => (
                <Button
                  key={index}
                  onClick={() => useSample(sample)}
                  variant="outline"
                  size="sm"
                  className="text-left h-auto p-2 whitespace-normal"
                >
                  {sample.substring(0, 100)}...
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="w-5 h-5" />
            <span>Basic Statistics</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {stats.characters.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.wordCounter.characters')}</div>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {stats.charactersNoSpaces.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.wordCounter.charactersNoSpaces')}</div>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {stats.words.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.wordCounter.words')}</div>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {stats.sentences.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.wordCounter.sentences')}</div>
            </div>
            <div className="text-center p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
              <div className="text-2xl font-bold text-pink-600 dark:text-pink-400">
                {stats.paragraphs.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.wordCounter.paragraphs')}</div>
            </div>
            <div className="text-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                {stats.lines.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.wordCounter.lines')}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reading Metrics */}
      {text && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Clock className="w-5 h-5" />
              <span>{t('tools.wordCounter.readingMetrics')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg">
                <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                  {stats.readingTime}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('tools.wordCounter.readingTime')}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {t('tools.wordCounter.readingSpeed')}
                </div>
              </div>
              <div className="text-center p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg">
                <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                  {stats.speakingTime}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('tools.wordCounter.speakingTime')}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {t('tools.wordCounter.speakingSpeed')}
                </div>
              </div>
              <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                  {stats.averageWordsPerSentence}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('tools.wordCounter.averageWordsPerSentence')}
                </div>
              </div>
              <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {stats.averageSentencesPerParagraph}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {t('tools.wordCounter.averageSentencesPerParagraph')}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Word Analysis */}
      {text && stats.mostCommonWords.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('tools.wordCounter.mostCommonWords')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.mostCommonWords.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                    <span className="font-medium">{index + 1}. {item.word}</span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">{item.count} {t('tools.wordCounter.times')}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('tools.wordCounter.wordLengthAnalysis')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('tools.wordCounter.longestWord')}:</span>
                  <div className="font-mono text-lg font-bold text-blue-600 dark:text-blue-400">
                    {stats.longestWord} ({stats.longestWord.length} {t('tools.wordCounter.chars')})
                  </div>
                </div>
                <div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('tools.wordCounter.shortestWord')}:</span>
                  <div className="font-mono text-lg font-bold text-green-600 dark:text-green-400">
                    {stats.shortestWord} ({stats.shortestWord.length} {t('tools.wordCounter.chars')})
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
